import type {
  ExecutionCandlesLabelOverlay,
  ExecutionCandlesOverlaysBundle,
  ExecutionCandlesPaneOverlay,
  ExecutionCandlesPriceOverlay,
  ExecutionOverlayKind,
} from "../types/executionCandlesOverlays";
import type { ExecutionCandle, ExecutionCandlesRegimeOverlay } from "../types/trade";
import { executionCandleRowTime } from "./executionCandlesNormalize";
import { executionCandleTimeToMillis } from "./vela/executionCandlesToVelaBars";

/** Empty-safe: pad/truncate label series to candle length; null → unknown. */
export function zipLabelOverlayToCandles(
  candles: readonly ExecutionCandle[] | null | undefined,
  overlay: ExecutionCandlesLabelOverlay | ExecutionCandlesRegimeOverlay | null | undefined
): Array<string | null> {
  const n = candles?.length ?? 0;
  const conditions = overlay?.conditions ?? [];
  const out: Array<string | null> = new Array(n);
  for (let i = 0; i < n; i += 1) {
    const raw = conditions[i];
    out[i] =
      typeof raw === "string" && raw.trim() ? raw.trim().toLowerCase() : null;
  }
  return out;
}

/** Bar times (epoch ms) aligned with prepared candles — empty-safe. */
export function zipBarTimesMs(
  candles: readonly ExecutionCandle[] | null | undefined
): number[] {
  const list = candles ?? [];
  const out: number[] = [];
  for (const c of list) {
    const ms = executionCandleTimeToMillis(executionCandleRowTime(c));
    if (ms != null) out.push(ms);
  }
  return out;
}

/**
 * Align overlay bundle series to candle count (pad with null / truncate).
 * Does not invent values — missing BE data stays null.
 */
export function normalizeOverlaysBundle(
  candles: readonly ExecutionCandle[] | null | undefined,
  overlays: ExecutionCandlesOverlaysBundle | null | undefined,
  regimeLegacy?: ExecutionCandlesRegimeOverlay | null
): ExecutionCandlesOverlaysBundle | null {
  if (!overlays && !regimeLegacy) return null;
  const n = candles?.length ?? 0;
  const regime =
    overlays?.regime ??
    (regimeLegacy
      ? {
          candle_timeframe: regimeLegacy.candle_timeframe,
          classifier_version: regimeLegacy.classifier_version,
          conditions: regimeLegacy.conditions,
          message: regimeLegacy.message,
        }
      : null);

  const padLabels = (
    o: ExecutionCandlesLabelOverlay | null | undefined
  ): ExecutionCandlesLabelOverlay | null => {
    if (!o) return null;
    const conditions = zipLabelOverlayToCandles(candles, o);
    return { ...o, conditions };
  };

  const padPrice = (
    o: ExecutionCandlesPriceOverlay | null | undefined
  ): ExecutionCandlesPriceOverlay | null => {
    if (!o?.series) return null;
    const series = o.series.slice(0, n);
    while (series.length < n) {
      series.push({});
    }
    return { ...o, series };
  };

  const padPanes = (
    o: ExecutionCandlesPaneOverlay | null | undefined
  ): ExecutionCandlesPaneOverlay | null => {
    if (!o?.series) return null;
    const series = o.series.slice(0, n);
    while (series.length < n) {
      series.push({});
    }
    return { ...o, series };
  };

  return {
    regime: padLabels(regime),
    direction: padLabels(overlays?.direction),
    direction_short: padLabels(overlays?.direction_short),
    session: padLabels(overlays?.session),
    adx_bucket: padLabels(overlays?.adx_bucket),
    rsi_bucket: padLabels(overlays?.rsi_bucket),
    atr_regime: padLabels(overlays?.atr_regime),
    squeeze: padLabels(overlays?.squeeze),
    bb_zone: padLabels(overlays?.bb_zone),
    bb_quartile: padLabels(overlays?.bb_quartile),
    price: padPrice(overlays?.price),
    panes: padPanes(overlays?.panes),
    sr: overlays?.sr ?? null,
  };
}

/** Serialize include_overlays query value. */
export function formatIncludeOverlaysParam(
  kinds: readonly ExecutionOverlayKind[] | null | undefined
): string | undefined {
  if (!kinds?.length) return undefined;
  const uniq = Array.from(new Set(kinds.map((k) => k.trim().toLowerCase()).filter(Boolean)));
  return uniq.length ? uniq.join(",") : undefined;
}
